import { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet-async";
import "../assets/css/theme.css";
import "../assets/css/main-style.css";
import { useDispatch, useSelector } from "react-redux";
import { signOutUser } from "../../store/slice/user";
import { toast } from "react-toastify";
import { emptyCart, removeFromCart } from "../../store/slice/cart";
import RegionDropdown from "./RegionDropdown";
import isValidURL from "../../ValidateUrl";
import { handleGetAllDishesOfCity } from "../../services/get_without_auth";
import { handleGetPendingOrdersForChef } from "../../services/order";
import { handleGetDeliveryRates } from "../../services/shef";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, ShoppingCart, Home, Info, User, Plus, CookingPot, ChefHat } from "lucide-react";
import { clearCurrentUser } from "../../store/slice/cart";
import { initializeUserCart } from "../../store/slice/cart";


// added comment to modify the file for git

const Header = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEmptyModalVisible, setEmptyModalVisible] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  
  // ---- Search bar Start
  const [allDishes, setAllDishes] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  //Search bar
  const [search, setSearch] = useState("");
  // radius (km) filter — uses browser geolocation
  const [radiusKm, setRadiusKm] = useState("");
  // delivery rate tiers for the max-charge estimate on radius options
  const [deliveryTiers, setDeliveryTiers] = useState([]);
  // matched dishes
  const [matchedDish, setMatchedDish] = useState([]);

  const { userInfo, authToken } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Keep the radius select in sync with the applied filter in the URL
  useEffect(() => {
    const r = new URLSearchParams(location.search).get("radius");
    setRadiusKm(r || "");
  }, [location.search]);

  // Handle click outside profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.id) {
      dispatch(initializeUserCart({ userId: user.id }));
    }
  }, []);

  // Chef Pending Order notification
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  useEffect(() => {
    let intervalId;

    const fetchPendingOrderCount = async () => {
      try {
        const pendingOrderResponse = await handleGetPendingOrdersForChef(
          userInfo?.id,
          authToken
        );
        setPendingOrderCount(pendingOrderResponse);
      } catch (error) {
        console.error("Error while fetching Pending orders \n", error);
      }
    };

    if (userInfo?.is_chef === 1 || userInfo?.is_admin === 1) {
      fetchPendingOrderCount();
      intervalId = setInterval(() => {
        fetchPendingOrderCount();
      }, 300000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [authToken, userInfo?.id, userInfo?.is_chef, userInfo?.is_admin]);

  // Fetch dishes for search bar
  useEffect(() => {
    const city = JSON.parse(localStorage.getItem("region"));
    const fetchAllDishes = async () => {
      try {
        setIsFetching(true);
        const dishResponse = await handleGetAllDishesOfCity(city?.id);
        setAllDishes(dishResponse);
      } catch (error) {
        console.error("Error while fetching dishes \n", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchAllDishes();
  }, []);

  // Fetch delivery rate tiers once — used for charge estimates on radius options
  useEffect(() => {
    (async () => {
      try {
        const res = await handleGetDeliveryRates();
        setDeliveryTiers(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        // estimates are optional — silently ignore
      }
    })();
  }, []);

  // Radius options — label shows the max configured delivery charge for that range
  const maxTierRange = deliveryTiers.length
    ? Math.max(...deliveryTiers.map((t) => parseFloat(t.range_to) || 0))
    : 0;
  const radiusOptions = [5, 10, 15, 20, 30].map((km) => {
    const charges = deliveryTiers
      .filter((t) => (parseFloat(t.range_to) || 0) <= km)
      .map((t) => parseFloat(t.charges) || 0);
    const est = charges.length ? Math.max(...charges) : null;
    const label =
      est !== null
        ? `≤ ${km} km~Rs. ${est}${km > maxTierRange ? "+" : ""}`
        : `Within ${km} km`;
    return { km, label };
  });

  // search submit — optional radius filter uses browser geolocation
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = search.trim();
    const km = parseFloat(radiusKm);

    const goToResults = (query = "") => {
      const base = trimmed ? `/search/${encodeURIComponent(trimmed)}` : "/search";
      navigate(`${base}${query}`);
      setSearch(""); // optional: clear search after navigating
    };

    if (km > 0) {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by your browser.");
        goToResults();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          goToResults(
            `?radius=${km}&lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`
          );
        },
        () => {
          toast.error("Location access denied. Searching without radius filter.");
          goToResults();
        }
      );
      return;
    }

    if (trimmed) {
      goToResults();
    }
  };

  // Function to handle Add Dish button click
  const handleAddDishClick = () => {
    navigate("/shef/my-menu", { 
      state: { openAddDishModal: true } 
    });
  };

  const handleSignOut = () => {
    dispatch(clearCurrentUser());
    dispatch(signOutUser());
    // dispatch(emptyCart());
    toast.success("Logout Successfully ");
    navigate("/");
    setIsProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const Modal = ({ isVisible, onClose, onConfirm }) => {
    if (!isVisible) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-6 max-w-sm w-full">
          <h2 className="text-xl font-bold mb-3">Are you sure?</h2>
          <p className="text-gray-600 mb-5">Do you really want to empty your bag?</p>
          <div className="flex gap-3">
            <button 
              onClick={onClose} 
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-red-600 hover:bg-red-600 text-white py-2 rounded-lg font-medium transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleEmptyCart = () => {
    dispatch(emptyCart());
    setEmptyModalVisible(false);
  };

  // Cart from -- Redux store
  const { cartItem } = useSelector((state) => state.cart);
  const subTotal = cartItem.reduce((accChef, chef) => {
    const chefTotal = chef.menu.reduce((accMenu, menu) => {
      return accMenu + (menu.unit_price || 0) * menu.quantity;
    }, 0);
    return accChef + chefTotal;
  }, 0);

  const cartItemCount = cartItem.reduce(
    (total, chef) => total + chef.menu.length,
    0
  );

  return (
    <>
      <Helmet>
        <title>Home | Shefs Cloud</title>
        <link href="assets/css/style.css" rel="stylesheet"></link>
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap"
          rel="stylesheet"
        ></link>
      </Helmet>

      {/* === NAVBAR === */}
      <nav className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left Section - Menu & Logo */}
            <div className="flex items-center space-x-2 md:space-x-3">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <Menu className="w-6 h-6 text-gray-600" />
              </button>

              <NavLink to="/" className="flex items-center flex-shrink-0">
                {/* Mobile logo - optimized size */}
                <img
                  src="/media/frontend/img/logo/new/-line-logo.png"
                  alt="Logo"
                  className="h-12 w-auto block md:hidden"
                />

                {/* Desktop logo */}
                <img
                  src="/media/frontend/img/logo/new/shefscloud_logo_2.png"
                  alt="Logo"
                  className="h-10 w-auto hidden md:block"
                />
              </NavLink>
            </div>

            {/* Center Section - Search (Desktop only) */}
            <form onSubmit={handleSearchSubmit} className="hidden md:flex flex-1 max-w-lg mx-8 items-center">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search dishes or shefs..."
                  className="w-full pl-10 pr-3 py-2 border border-red-600 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                />
              </div>
              <select
                value={radiusKm}
                onChange={(e) => setRadiusKm(e.target.value)}
                title="Search radius for dish/shef — shows max delivery charge estimate, uses your current location"
                className="flex-shrink-0 w-40  px-2 py-2 border-y border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-600 cursor-pointer"
              >
                <option value="">Distance (km)</option>
                {radiusOptions.map((o) => (
                  <option key={o.km} value={o.km}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                className="bg-red-600 text-white px-4 py-2 rounded-r-lg whitespace-nowrap"
              >
                Search
              </button>
            </form>

            {/* Right Section - Actions */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Region Dropdown (Desktop) */}
              <div className="hidden md:flex items-center">
                <RegionDropdown />
              </div>

              {/* Logic: "Become a Shef" for Guests/Non-Chefs */}
              {(!userInfo || userInfo?.is_chef !== 1) && (
                <>
                  {/* Desktop: Full Red Button */}
                  <NavLink
                    to="/become-a-chef"
                    className="bg-red-600 hover:bg-red-700 !text-white px-4 py-2 rounded-lg font-medium transition-colors hidden md:block whitespace-nowrap"
                  >
                    Become a Shef
                  </NavLink>
                  {/* Mobile: Compact Button */}
                 {/* <NavLink
                    to="/become-a-chef"
                    className="md:hidden bg-red-600 hover:bg-red-700 !text-white px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    Become Shef
                  </NavLink>*/}
                </>
              )}

              {/* Guest User Buttons - Only Sign In */}
              {!userInfo && (
                <NavLink
                  to="/login"
                  className="text-gray-600 hover:text-red-600 px-2.5 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap text-xs sm:text-sm flex-shrink-0"
                >
                  Sign In
                </NavLink>
              )}

              {/* Logged In User Actions */}
              {userInfo && (
                <>
                  {/* Add Dish Button - Chefs Only */}
                  {(userInfo.is_chef === 1 || userInfo.is_admin === 1) && (
                    <>
                      {/* Desktop: Full Green Button */}
                      <button
                        onClick={handleAddDishClick}
                        className="hidden md:flex bg-green-600 hover:bg-green-700 !text-white px-4 py-2 rounded-lg font-medium transition-colors items-center gap-1.5 flex-shrink-0"
                        title="Add New Dish"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add New Dish</span>
                      </button>

                      {/* Mobile: Small Cooking Pot Icon */}
                      <button
                        onClick={handleAddDishClick}
                        className="md:hidden text-gray-600 hover:text-green-600 p-2 flex-shrink-0"
                        title="Add New Dish"
                      >
                         <CookingPot className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {/* Pending Orders Notification */}
                  {(userInfo.is_chef === 1 || userInfo.is_admin === 1) && pendingOrderCount > 0 && (
                    <div className="relative flex-shrink-0">
                      <NavLink to="/shef/order" className="text-gray-600 hover:text-red-600">
                        <div className="relative p-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="text-gray-600 hover:text-red-600"
                          >
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                          </svg>
                          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {pendingOrderCount}
                          </span>
                        </div>
                      </NavLink>
                    </div>
                  )}

                  {/* Profile Dropdown */}
                  <div className="relative flex-shrink-0" ref={profileDropdownRef}>
                    <button 
                      onClick={toggleProfileDropdown}
                      className="flex items-center space-x-1 focus:outline-none"
                    >
                      <img
                        src={
                          userInfo.profile_pic && isValidURL(userInfo.profile_pic)
                            ? userInfo.profile_pic
                            : "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
                        }
                        className="w-8 h-8 rounded-full object-cover"
                        alt="User"
                      />
                    </button>
                    {isProfileDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {(userInfo.is_chef === 1 || userInfo.is_admin === 1) && (
                            <NavLink
                              to="/shef/dashboard"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => setIsProfileDropdownOpen(false)}
                            >
                              Dashboard
                            </NavLink>
                          )}
                          <NavLink
                            to="/order"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            Orders
                          </NavLink>
                          {(userInfo.is_chef === 1 || userInfo.is_admin === 1) && (
                            <NavLink
                              to="/shef/order"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => setIsProfileDropdownOpen(false)}
                            >
                              Shef Orders
                            </NavLink>
                          )}
                          <NavLink
                            to={userInfo.is_chef === 1 || userInfo.is_admin === 1 ? "/shef/profile" : "/profile"}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            Account
                          </NavLink>
                          <button
                            onClick={handleSignOut}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Shopping Cart */}
              <div className="relative flex-shrink-0">
                <NavLink to="/cart" className="text-gray-600 hover:text-red-600">
                  <ShoppingCart className="w-6 h-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </NavLink>
                
                {/* Cart Dropdown */}
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-50 hidden group-hover:block">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold text-lg">Your Order</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto p-2">
                    {cartItem.map((chef, chefIndex) =>
                      chef.menu.map((product, menuIndex) => (
                        <div
                          key={menuIndex}
                          className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src={
                                product.logo && isValidURL(product.logo)
                                  ? product.logo
                                  : "https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/No-Image-Placeholder.svg/330px-No-Image-Placeholder.svg.png?20200912122019"
                              }
                              alt="Dish"
                              className="w-12 h-12 object-cover rounded-md"
                            />
                            <div>
                              <p className="font-medium">
                                <span className="text-gray-500">
                                  {product.quantity}x{" "}
                                </span>
                                {product.name}
                              </p>
                              <p className="text-red-600 font-semibold">
                                {product?.unit_price?.toLocaleString("en-PK", {
                                  style: "currency",
                                  currency: "PKR",
                                })}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(
                                removeFromCart({
                                  chefIndex,
                                  menuIndex,
                                })
                              );
                            }}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      ))
                    )}
                    {cartItem.length === 0 && (
                      <p className="text-center py-4 text-gray-500">Your cart is empty</p>
                    )}
                  </div>
                  {cartItem.length > 0 && (
                    <>
                      <div className="p-4 border-t">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-semibold">Subtotal:</span>
                          <span className="font-bold">
                            {subTotal.toLocaleString("en-PK", {
                              style: "currency",
                              currency: "PKR",
                            })}
                          </span>
                        </div>
                        <button
                          type="button"
                          disabled={cartItem?.length === 0}
                          className={`w-full py-2 rounded-lg font-medium ${
                            cartItem?.length === 0
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "text-red-600 hover:bg-red-50"
                          }`}
                          onClick={() => setEmptyModalVisible(true)}
                        >
                          Empty bag
                        </button>
                      </div>
                      <div className="p-4 border-t">
                        <NavLink
                          to="/cart"
                          className="block w-full bg-red-600 hover:bg-red-600 text-white text-center py-2 rounded-lg font-medium transition-colors"
                        >
                          Proceed to Cart
                        </NavLink>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* === SIDEBAR === */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />

            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-50"
            >
              <div className="relative h-48 bg-gradient-to-br from-red-600 to-red-600 overflow-hidden">
                <img
                  src="/media/frontend/img/about/blog/255x200/about-section-1.jpg"
                  alt="Food background"
                  className="absolute inset-0 w-full h-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>

                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <h1 className="text-xl font-bold text-white">Shefs Cloud</h1>
                  </div>
                  <div className="bg-red-400/90 backdrop-blur-sm px-3 py-1 rounded-full w-fit">
                    <span className="text-white text-sm font-semibold">Top Rated Shef</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-2">
                <NavLink
                  to="/"
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-gray-50 hover:text-red-600 text-gray-600"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <Home className="w-5 h-5" />
                  <span className="font-medium">Home</span>
                </NavLink>
                <NavLink
                  to="/about"
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-gray-50 hover:text-red-600 text-gray-600"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <Info className="w-5 h-5" />
                  <span className="font-medium">About Us</span>
                </NavLink>
                
                {/* Condition: Show if user is NOT logged in OR (logged in AND not a chef) */}
                {(!userInfo || userInfo?.is_chef !== 1) && (
                  <NavLink
                    to="/become-a-chef"
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-gray-50 hover:text-red-600 text-gray-600"
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    <User className="w-5 h-5" />
                    <span className="font-medium">Become A Shef</span>
                  </NavLink>
                )}
                
                {/* Add Dish in Sidebar - Only for chefs */}
                {userInfo && (userInfo.is_chef === 1 || userInfo.is_admin === 1) && (
                  <button
                    onClick={() => {
                      handleAddDishClick();
                      setIsSidebarOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-green-50 hover:text-green-600 text-gray-600"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="font-medium">Add Dish</span>
                  </button>
                )}
                
                <div className="lg:hidden w-full">
                  <RegionDropdown />
                </div>
              </div>

              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-2">Need Help?</h4>
                  <p className="text-sm text-red-600 mb-3">Contact our support team</p>
                  <button
                    onClick={() => {
                      setIsSidebarOpen(false);

                      const email = "info@shefscloud.com";
                      const subject = encodeURIComponent("Support Request – Shefs Cloud");
                      const body = encodeURIComponent(`Hello Shefs Cloud Support Team,

I need assistance with...

Thank you.`);

                      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

                      window.open(gmailUrl, "_blank") ||
                        (window.location.href = `mailto:${email}?subject=${subject}&body=${body}`);
                    }}
                    className="block w-full bg-red-600 hover:bg-red-600 text-white py-2 rounded-lg font-medium text-center transition-colors"
                  >
                    Contact Support
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer for fixed navbar */}
      <div className="h-16"></div>
      
      <Modal
        isVisible={isEmptyModalVisible}
        onClose={() => setEmptyModalVisible(false)}
        onConfirm={handleEmptyCart}
      />
    </>
  );
};

export default Header;